import {
  achievementById,
  achievementChapters,
  achievementLampXp,
  chicagoDateKey,
  chicagoWeekdayLetter,
  DAILY_LAMP_TRACK,
  dailyTaskById,
  formatDailyCountdown,
  isAchievementMet,
  ITEMS,
  itemSkillId,
  lampRewardCopy,
  lampXp,
  loginReward,
  msUntilDailyReset,
  recentDateKeys,
  RECIPES,
  recipeSkillId,
  SKILL_IDS,
  SKILLS,
  skillColorCss,
  streakInfo,
  totalLevel,
  VAULT_SLOTS,
  xpForLevel,
  xpToNextLevel,
  type AchievementDef,
  type ItemId,
  type NeighborhoodEntry,
  type ClientMessage,
  type PlayerAction,
  type PlayerPrivateState,
  type ServerMessage,
  type SkillId,
  type TravelDestination,
  type MapData,
} from "@dogdell/shared";
import { DOG_BREEDS, DOG_PACK_ROOT } from "../game/DogBreeds";
import { enableDrag } from "./dragPanel";
import { filledItemIcon, iconSvg } from "./icons";
import { bakeStylizedTravelMap } from "./mapTerrain";

const skillsList = document.querySelector("#skills-list")!;
const inventoryGrid = document.querySelector("#inventory-grid")!;
const chatLog = document.querySelector("#chat-log")!;
const toasts = document.querySelector("#toasts")!;
const itemTooltip = document.querySelector("#item-tooltip") as HTMLElement | null;
const itemTooltipName = document.querySelector("#item-tooltip-name") as HTMLElement | null;
const itemTooltipDesc = document.querySelector("#item-tooltip-desc") as HTMLElement | null;
const actionProgress = document.querySelector("#action-progress") as HTMLElement | null;
const actionProgressIco = document.querySelector("#action-progress-ico") as HTMLElement | null;
const actionProgressLabel = document.querySelector("#action-progress-label") as HTMLElement | null;
const actionProgressFill = document.querySelector("#action-progress-fill") as HTMLElement | null;
const actionProgressTrack = actionProgress?.querySelector(".action-progress-track") as HTMLElement | null;
const xpPops = document.querySelector("#xp-pops") as HTMLElement | null;
const locationBanner = document.querySelector("#location-banner") as HTMLElement | null;
const locationBannerName = document.querySelector("#location-banner-name") as HTMLElement | null;
const teleportVeil = document.querySelector("#teleport-veil") as HTMLElement | null;
const statusEl = document.querySelector("#connection-status")!;
const gameRoot = document.querySelector("#game-root") as HTMLElement | null;
const gameHud = document.querySelector("#game-hud") as HTMLElement | null;
const gamePanel = document.querySelector("#game-panel") as HTMLElement | null;
const panelTitle = document.querySelector("#game-panel-title") as HTMLElement | null;
const panelGlyph = document.querySelector("#game-panel-glyph") as HTMLElement | null;
const totalLevelEl = document.querySelector("#skills-total-level") as HTMLElement | null;
const boostMeter = document.querySelector("#boost-meter") as HTMLElement | null;
const modalRoot = document.querySelector("#modal-root") as HTMLElement;
const dailyWidget = document.querySelector("#daily-widget") as HTMLElement | null;
const dailyLauncher = document.querySelector("#daily-launcher") as HTMLElement | null;
const dailyChallenge = document.querySelector("#daily-challenge") as HTMLElement | null;
const dailyChallengeBody = document.querySelector("#daily-challenge-body") as HTMLElement | null;
const dailyLogin = document.querySelector("#daily-login") as HTMLElement | null;
const dailyLoginBody = document.querySelector("#daily-login-body") as HTMLElement | null;
const dailyLauncherTimer = document.querySelector("#daily-launcher-timer") as HTMLElement | null;
const dailyChallengeTimer = document.querySelector("#daily-challenge-timer") as HTMLElement | null;
const dailyLauncherBadge = document.querySelector("#daily-launcher-badge") as HTMLElement | null;
const goalsMenuBadge = document.querySelector("#goals-menu-badge") as HTMLElement | null;
const goalsMenuBtn = document.querySelector("#btn-achievements") as HTMLButtonElement | null;
const nextStepsEl = document.querySelector("#next-steps") as HTMLElement | null;
const gameMenubar = document.querySelector("#game-menubar") as HTMLElement | null;
const gameChat = document.querySelector("#game-chat") as HTMLElement | null;

const TAB_PAGES = ["skills", "bag"] as const;
type GameTab = (typeof TAB_PAGES)[number];

const TAB_META: Record<GameTab, { title: string; glyph: string }> = {
  skills: { title: "Skills", glyph: "bone" },
  bag: { title: "Bag", glyph: "bag" },
};

const SKILL_GLYPH: Record<SkillId, string> = {
  digging: "shovel",
  fetching: "ball",
  sniffing: "berry",
  begging: "sit",
  toycraft: "hammer",
  treatcraft: "treat",
};

function skillTintClass(id: SkillId): string {
  return `skill-tint-${id}`;
}

function skillOutlineClass(id: SkillId): string {
  return `skill-outline-${id}`;
}

export class Hud {
  private lastHash = "";
  private you: PlayerPrivateState | null = null;
  private parkMap: MapData | null = null;
  private claimSkill: SkillId = "digging";
  private tab: GameTab | null = "skills";
  private lastActionKey = "";
  private lastActionPct = -1;
  private dailyOpen = false;
  private dailyTimerId = 0;
  private goalsChapter = 0;
  private goalsSelectedId: string | null = null;
  private travelModalOpen = false;
  private bankModalOpen = false;
  private inventoryDragMoved = false;
  private goalsModalOpen = false;
  private teleportLiftTimer = 0;
  private teleportHideTimer = 0;
  private locationBannerTimer = 0;
  private itemTipAnchor: HTMLElement | null = null;
  /** Active XP popups keyed by skill - same skill refreshes instead of stacking. */
  private readonly xpPopBySkill = new Map<
    SkillId,
    { el: HTMLElement; amount: number; removeTimer: number; raf: number }
  >();

  constructor(private readonly send: (msg: ClientMessage) => void) {
    document.querySelector("#btn-travel")?.addEventListener("click", () => this.send({ type: "openTravel" }));
    document.querySelector("#btn-achievements")?.addEventListener("click", () => this.openAchievements());
    document.querySelector("#btn-cosmetics")?.addEventListener("click", () => this.openCosmetics());
    for (const tab of TAB_PAGES) {
      document.querySelector(`#btn-${tab}`)?.addEventListener("click", () => this.showTab(tab));
    }
    document.querySelector("#btn-panel-close")?.addEventListener("click", () => this.closePanel());
    document.querySelector("#daily-challenge-min")?.addEventListener("click", () => this.minimizeDailyChallenge());
    document.querySelector("#daily-login-close")?.addEventListener("click", () => this.closeLogin());

    dailyWidget?.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (target.closest("[data-open-daily]")) {
        this.openDailyChallenge();
      }
      if (target.closest("[data-claim]")) {
        this.openLamp("daily");
      }
    });

    dailyChallengeBody?.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (target.closest("[data-claim]")) {
        this.openLamp("daily");
      }
      if (target.closest("[data-open-login]")) {
        this.openLogin();
      }
    });

    dailyLoginBody?.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (target.closest("#claim-login")) {
        this.send({ type: "claimLogin" });
      }
    });

    this.bindDraggables();
    this.paintStaticIcons();
    this.startDailyTimer();
  }

  private paintStaticIcons(): void {
    const map: Record<string, string> = {
      "ico-bag": "bag",
      "ico-bone": "bone",
      "ico-paw": "paw",
      "ico-portal": "portal",
      "ico-calendar": "calendar",
      "ico-sun": "sun",
      "ico-book": "book",
      "ico-collar": "collar",
      "ico-shovel": "shovel",
      "ico-ball": "ball",
      "ico-bark": "paw",
      "ico-sparkle": "sparkle",
    };
    for (const [cls, kind] of Object.entries(map)) {
      document.querySelectorAll(`.${cls}`).forEach((el) => {
        const size = el.classList.contains("mb-ico") || el.closest(".mb-ico") ? 22 : 18;
        el.innerHTML = iconSvg(kind, size);
      });
    }
    if (panelGlyph) {
      panelGlyph.innerHTML = iconSvg("bag");
    }
  }

  private bindDraggables(): void {
    if (!gameRoot) {
      return;
    }
    const pairs: {
      panel: HTMLElement | null;
      handleSel: string;
      key: string;
      onTap?: () => void;
    }[] = [
      { panel: gamePanel, handleSel: '[data-drag="panel"]', key: "dogdell-ui-panel" },
      { panel: gameMenubar, handleSel: '[data-drag="menubar"]', key: "dogdell-ui-menubar" },
      { panel: gameChat, handleSel: '[data-drag="chat"]', key: "dogdell-ui-chat" },
      {
        panel: dailyLauncher,
        handleSel: '[data-drag="daily-launcher"]',
        key: "dogdell-ui-daily-launcher",
        onTap: () => this.openDailyChallenge(),
      },
      {
        panel: dailyChallenge,
        handleSel: '[data-drag="daily-challenge"]',
        key: "dogdell-ui-daily-challenge",
      },
      {
        panel: dailyLogin,
        handleSel: '[data-drag="daily-login"]',
        key: "dogdell-ui-daily-login",
      },
    ];
    for (const { panel, handleSel, key, onTap } of pairs) {
      if (!panel) {
        continue;
      }
      const handle =
        panel.matches(handleSel) ? panel : panel.querySelector<HTMLElement>(handleSel);
      if (handle) {
        enableDrag({ handle, panel, bounds: gameRoot, storageKey: key, onTap });
      }
    }
    dailyLauncher?.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.openDailyChallenge();
      }
    });
    // Default launcher spot if never moved.
    if (dailyLauncher && !dailyLauncher.style.left) {
      dailyLauncher.style.left = "12px";
      dailyLauncher.style.top = "12px";
    }
    if (dailyChallenge && !dailyChallenge.style.left) {
      dailyChallenge.style.left = "12px";
      dailyChallenge.style.top = "12px";
    }
    if (dailyLogin && !dailyLogin.style.left) {
      dailyLogin.style.left = "36px";
      dailyLogin.style.top = "48px";
    }
  }

  private startDailyTimer(): void {
    const tick = () => this.paintDailyTimers();
    tick();
    window.clearInterval(this.dailyTimerId);
    this.dailyTimerId = window.setInterval(tick, 1000);
  }

  private paintDailyTimers(): void {
    const text = formatDailyCountdown(msUntilDailyReset());
    if (dailyLauncherTimer) {
      dailyLauncherTimer.textContent = text;
    }
    if (dailyChallengeTimer) {
      dailyChallengeTimer.textContent = text;
    }
  }

  show(): void {
    if (gameHud) {
      gameHud.hidden = false;
      gameHud.classList.add("is-panel-closed");
      // Next frame so the opacity transition runs from 0 → 1.
      requestAnimationFrame(() => {
        gameHud.classList.add("is-visible");
      });
    }
    this.tab = null;
    this.syncTabButtons();
  }

  private closePanel(): void {
    this.tab = null;
    gameHud?.classList.add("is-panel-closed");
    if (gamePanel) {
      gamePanel.hidden = true;
    }
    this.syncTabButtons();
  }

  private showTab(tab: GameTab): void {
    if (!gameHud) {
      return;
    }
    if (this.tab === tab && !gameHud.classList.contains("is-panel-closed")) {
      this.closePanel();
      return;
    }
    this.tab = tab;
    gameHud.classList.remove("is-panel-closed");
    if (gamePanel) {
      gamePanel.hidden = false;
    }
    const meta = TAB_META[tab];
    if (panelTitle) {
      panelTitle.textContent = meta.title;
    }
    if (panelGlyph) {
      panelGlyph.innerHTML = iconSvg(meta.glyph);
      panelGlyph.dataset.glyph = meta.glyph;
    }
    for (const id of TAB_PAGES) {
      const page = document.querySelector(`#panel-${id}`) as HTMLElement | null;
      if (page) {
        page.hidden = id !== tab;
      }
    }
    this.syncTabButtons();
  }

  private syncTabButtons(): void {
    for (const id of TAB_PAGES) {
      const btn = document.querySelector(`#btn-${id}`) as HTMLButtonElement | null;
      const active = this.tab === id && !gameHud?.classList.contains("is-panel-closed");
      btn?.classList.toggle("is-active", active);
      btn?.setAttribute("aria-pressed", String(active));
    }
  }

  setStatus(text: string): void {
    statusEl.textContent = text;
  }

  handleServer(message: ServerMessage): void {
    if (message.type === "welcome" || message.type === "zoneChange" || message.type === "spectatorWelcome") {
      this.parkMap = message.map;
    }
    if (message.type === "travelMenu") {
      this.openTravel(message);
    }
    if (message.type === "closeTravel" && this.travelModalOpen) {
      this.closeModal();
    }
    if (message.type === "neighborhoodRoster") {
      this.openRoster(message.entries);
    }
    if (message.type === "openBank") {
      this.openBank();
    }
    if (message.type === "openCraft") {
      this.openCraft();
    }
    if (message.type === "dailyUpdated" && this.you) {
      this.you.dailyChallenge = message.daily;
      this.renderDaily(this.you);
    }
    if (message.type === "loginUpdated" && this.you) {
      this.you.dailyLoginCalendar = message.login;
      this.renderDaily(this.you);
      if (dailyLogin && !dailyLogin.hidden) {
        this.renderLoginPanel(this.you);
      }
    }
    if (message.type === "confirmPortal") {
      this.confirm(message.hostName, () => this.send({ type: "enterPortal", portalId: message.portalId }));
    }
  }

  render(you: PlayerPrivateState): void {
    this.you = you;
    const hash = JSON.stringify({
      skills: you.skills,
      inventory: you.inventory,
      vault: you.vault,
      daily: you.dailyChallenge,
      login: you.dailyLoginCalendar,
      next: you.nextSteps,
      boosts: you.boosts,
      ftue: you.ftue,
      achievements: you.achievementBook,
      visited: you.visitedAreas,
      gathered: you.lifetimeGathered,
      crafted: you.lifetimeCrafted,
      dailyOpen: this.dailyOpen,
    });
    if (hash === this.lastHash) {
      return;
    }
    this.lastHash = hash;
    this.renderSkills(you);
    this.renderInventory(you);
    this.renderDaily(you);
    this.renderNextSteps(you);
    this.renderBoosts(you);
    this.renderGoalsBadge(you);
    if (totalLevelEl) {
      totalLevelEl.innerHTML = `Total level <strong>${totalLevel(you.skills)}</strong>`;
    }
    if (this.bankModalOpen) {
      this.openBank();
    }
  }

  pushBark(name: string, text: string): void {
    const line = document.createElement("p");
    line.innerHTML = `<strong>${escapeHtml(name)}</strong> ${escapeHtml(text)}`;
    chatLog.append(line);
    chatLog.scrollTop = chatLog.scrollHeight;
    while (chatLog.children.length > 24) {
      chatLog.firstElementChild?.remove();
    }
  }

  toast(message: string): void {
    const el = document.createElement("div");
    el.className = "toast";
    el.textContent = message;
    toasts.append(el);
    window.setTimeout(() => el.remove(), 3200);
  }

  /** Instant parchment cover so the teleport snap never paints mid-frame. */
  beginTeleportCover(): void {
    if (!teleportVeil) {
      return;
    }
    window.clearTimeout(this.teleportLiftTimer);
    window.clearTimeout(this.teleportHideTimer);
    teleportVeil.hidden = false;
    teleportVeil.classList.remove("is-cover");
    void teleportVeil.offsetWidth;
    teleportVeil.classList.add("is-cover");
  }

  /** Hold briefly on the new scene, then lift the cover and show where you landed. */
  endTeleportCover(placeName?: string): void {
    if (!teleportVeil) {
      if (placeName) {
        this.showLocationBanner(placeName);
      }
      return;
    }
    teleportVeil.hidden = false;
    teleportVeil.classList.add("is-cover");
    window.clearTimeout(this.teleportLiftTimer);
    window.clearTimeout(this.teleportHideTimer);
    this.teleportLiftTimer = window.setTimeout(() => {
      teleportVeil.classList.remove("is-cover");
      if (placeName) {
        this.showLocationBanner(placeName);
      }
      this.teleportHideTimer = window.setTimeout(() => {
        if (!teleportVeil.classList.contains("is-cover")) {
          teleportVeil.hidden = true;
        }
      }, 520);
    }, 320);
  }

  /** Top-center place name after a travel warp. */
  showLocationBanner(placeName: string): void {
    if (!locationBanner || !locationBannerName || !placeName.trim()) {
      return;
    }
    window.clearTimeout(this.locationBannerTimer);
    locationBannerName.textContent = placeName.trim();
    locationBanner.hidden = false;
    locationBanner.style.animation = "none";
    void locationBanner.offsetWidth;
    locationBanner.style.animation = "";
    this.locationBannerTimer = window.setTimeout(() => {
      locationBanner.hidden = true;
    }, 2600);
  }

  /** Hover tip for bag / bank slots (fixed so scroll grids don't clip it). */
  private showItemTip(anchor: HTMLElement, itemId: ItemId, quantity?: number): void {
    if (!itemTooltip || !itemTooltipName || !itemTooltipDesc) {
      return;
    }
    const item = ITEMS[itemId];
    if (!item) {
      return;
    }
    this.itemTipAnchor = anchor;
    itemTooltipName.textContent =
      quantity != null && quantity > 1 ? `${item.name} ×${quantity}` : item.name;
    itemTooltipDesc.textContent = item.description;
    itemTooltip.hidden = false;
    this.placeItemTip(anchor);
    requestAnimationFrame(() => {
      if (this.itemTipAnchor === anchor) {
        this.placeItemTip(anchor);
      }
    });
  }

  private placeItemTip(anchor: HTMLElement): void {
    if (!itemTooltip) {
      return;
    }
    const tip = itemTooltip;
    const rect = anchor.getBoundingClientRect();
    const tipW = tip.offsetWidth || 160;
    const tipH = tip.offsetHeight || 48;
    let left = rect.left + rect.width / 2 - tipW / 2;
    let top = rect.top - tipH - 8;
    left = Math.max(8, Math.min(left, window.innerWidth - tipW - 8));
    if (top < 8) {
      top = rect.bottom + 8;
    }
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
  }

  private hideItemTip(anchor?: HTMLElement): void {
    if (anchor && this.itemTipAnchor !== anchor) {
      return;
    }
    this.itemTipAnchor = null;
    if (itemTooltip) {
      itemTooltip.hidden = true;
    }
  }

  private bindItemTip(el: HTMLElement, itemId: ItemId, quantity?: number): void {
    el.addEventListener("pointerenter", () => this.showItemTip(el, itemId, quantity));
    el.addEventListener("pointerleave", () => this.hideItemTip(el));
    el.addEventListener("focus", () => this.showItemTip(el, itemId, quantity));
    el.addEventListener("blur", () => this.hideItemTip(el));
  }

  /**
   * Top-of-screen L→R meter while dig / fetch / craft (etc.) is in progress.
   * Call every frame from the park loop.
   */
  tickAction(action: PlayerAction | undefined | null): void {
    if (!actionProgress || !actionProgressFill) {
      return;
    }
    if (!action) {
      if (!actionProgress.hidden) {
        actionProgress.hidden = true;
        this.lastActionKey = "";
        this.lastActionPct = -1;
      }
      return;
    }

    const key = `${action.kind}:${action.skillId}:${action.startedAt}:${action.durationMs}`;
    if (key !== this.lastActionKey) {
      this.lastActionKey = key;
      this.lastActionPct = -1;
      actionProgress.hidden = false;
      const def = SKILLS[action.skillId];
      const hex = skillColorCss(action.skillId);
      if (actionProgressIco) {
        actionProgressIco.innerHTML = iconSvg(SKILL_GLYPH[action.skillId], 18);
        actionProgressIco.className = skillTintClass(action.skillId);
        actionProgressIco.style.color = hex;
      }
      if (actionProgressLabel) {
        actionProgressLabel.textContent = def.verb;
      }
      actionProgressFill.style.background = `linear-gradient(90deg, ${hex}, #fff8e1)`;
    }

    const t = Math.min(1, Math.max(0, (Date.now() - action.startedAt) / Math.max(1, action.durationMs)));
    const pct = Math.round(t * 100);
    if (pct === this.lastActionPct) {
      return;
    }
    this.lastActionPct = pct;
    actionProgressFill.style.width = `${pct}%`;
    actionProgressTrack?.setAttribute("aria-valuenow", String(pct));
  }

  /** Radial XP popup: skill icon, level, fill toward next level, XP remaining.
   * Same-skill gains refresh the existing circle instead of stacking another. */
  showXpGain(message: Extract<ServerMessage, { type: "xp" }>): void {
    if (!xpPops) {
      return;
    }
    const def = SKILLS[message.skillId];
    const hex = skillColorCss(message.skillId);
    const intoLevel = xpForLevel(message.level + 1) - xpForLevel(message.level) || 1;
    const remaining = message.level >= 99 ? 0 : xpToNextLevel(message.totalXp);
    const have = message.level >= 99 ? 1 : 1 - remaining / intoLevel;

    const priorXp = Math.max(0, message.totalXp - message.amount);
    const priorLevel = this.you?.skills[message.skillId]?.level ?? message.level;
    let from = have;
    if (priorLevel === message.level && message.level < 99) {
      const priorRemaining = xpToNextLevel(priorXp);
      from = 1 - priorRemaining / intoLevel;
    } else if (message.leveledUp) {
      from = 0;
    }
    from = Math.min(1, Math.max(0, from));

    const existing = this.xpPopBySkill.get(message.skillId);
    let el: HTMLElement;
    let amount: number;
    if (existing) {
      el = existing.el;
      amount = existing.amount + message.amount;
      window.clearTimeout(existing.removeTimer);
      cancelAnimationFrame(existing.raf);
      // Restart enter/exit timeline so refresh doesn't mid-fade the pop.
      el.style.animation = "none";
      void el.offsetWidth;
      el.style.animation = "";
      el.classList.remove("is-refresh");
      void el.offsetWidth;
      el.classList.add("is-refresh");
    } else {
      el = document.createElement("div");
      el.className = `xp-pop ${skillTintClass(message.skillId)}`;
      el.dataset.skill = message.skillId;
      amount = message.amount;
      xpPops.prepend(el);
      while (xpPops.children.length > 3) {
        const last = xpPops.lastElementChild as HTMLElement | null;
        if (!last) {
          break;
        }
        const skill = last.dataset.skill as SkillId | undefined;
        if (skill) {
          const entry = this.xpPopBySkill.get(skill);
          if (entry) {
            window.clearTimeout(entry.removeTimer);
            cancelAnimationFrame(entry.raf);
            this.xpPopBySkill.delete(skill);
          }
        }
        last.remove();
      }
    }

    el.innerHTML = `
      <div class="xp-pop-ring">
        <div class="xp-pop-core">
          <span class="xp-pop-ico">${iconSvg(SKILL_GLYPH[message.skillId], 36)}</span>
          <strong class="xp-pop-level">${message.level}</strong>
        </div>
      </div>
      <div class="xp-pop-meta">
        <span class="xp-pop-gain">+${amount} ${escapeHtml(def.name)}</span>
        <span class="xp-pop-need">${
          message.level >= 99
            ? "Max level"
            : `${remaining.toLocaleString()} XP to level ${message.level + 1}`
        }</span>
      </div>
    `;
    const ring = el.querySelector(".xp-pop-ring") as HTMLElement;
    const paint = (t: number) => {
      const deg = Math.round(t * 360);
      ring.style.background = `conic-gradient(${hex} ${deg}deg, rgba(80,56,32,0.22) 0)`;
    };
    paint(from);

    const started = performance.now();
    const duration = 650;
    let raf = 0;
    const tick = (now: number) => {
      const u = Math.min(1, (now - started) / duration);
      const eased = 1 - (1 - u) * (1 - u);
      paint(from + (have - from) * eased);
      if (u < 1) {
        raf = requestAnimationFrame(tick);
        const live = this.xpPopBySkill.get(message.skillId);
        if (live) {
          live.raf = raf;
        }
      }
    };
    raf = requestAnimationFrame(tick);

    const removeTimer = window.setTimeout(() => {
      el.remove();
      this.xpPopBySkill.delete(message.skillId);
    }, 3200);

    this.xpPopBySkill.set(message.skillId, { el, amount, removeTimer, raf });

    if (this.you) {
      this.you.skills[message.skillId] = { xp: message.totalXp, level: message.level };
      this.you.totalLevel = message.totalLevel;
      this.lastHash = "";
      this.render(this.you);
    }
  }

  private renderSkills(you: PlayerPrivateState): void {
    skillsList.replaceChildren();
    for (const id of SKILL_IDS) {
      const def = SKILLS[id];
      const skill = you.skills[id];
      const row = document.createElement("div");
      row.className = `skill-tile ${skillTintClass(id)}`;
      row.title = def.name;
      const intoLevel = xpForLevel(skill.level + 1) - xpForLevel(skill.level) || 1;
      const have = skill.level >= 99 ? 1 : 1 - xpToNextLevel(skill.xp) / intoLevel;
      row.innerHTML = `
        <div class="skill-row-head">
          <span class="skill-ico">${iconSvg(SKILL_GLYPH[id])}</span>
          <div class="skill-copy">
            <span class="skill-name">${escapeHtml(def.name)}</span>
            <strong class="skill-level">${skill.level}</strong>
          </div>
        </div>
        <div class="xp-track"><div class="xp-fill" style="width:${Math.round(have * 100)}%;background:${skillColorCss(id)}"></div></div>
      `;
      skillsList.append(row);
    }
  }

  private renderInventory(you: PlayerPrivateState): void {
    this.hideItemTip();
    inventoryGrid.replaceChildren();
    you.inventory.forEach((slot, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "inventory-slot";
      button.dataset.slot = String(index);
      if (slot) {
        const item = ITEMS[slot.itemId];
        const skillId = itemSkillId(slot.itemId);
        button.className = skillId
          ? `inventory-slot ${skillOutlineClass(skillId)}`
          : "inventory-slot";
        button.setAttribute("aria-label", item.name);
        const qty = slot.quantity > 1 ? `<span class="qty">${slot.quantity}</span>` : "";
        button.innerHTML = `<span class="slot-glyph">${itemGlyph(slot.itemId)}</span>${qty}`;
        this.bindItemTip(button, slot.itemId, slot.quantity);
        this.bindInventoryDnD(button, index, {
          filled: true,
          onActivate: () => {
            if (item.consumable) {
              this.send({ type: "useItem", slotIndex: index });
            }
          },
        });
      } else {
        this.bindInventoryDnD(button, index, { filled: false });
      }
      inventoryGrid.append(button);
    });
  }

  /** Drag-reorder bag slots (HTML5 DnD). Suppresses click after a successful drop. */
  private bindInventoryDnD(
    button: HTMLButtonElement,
    index: number,
    opts: { filled: boolean; onActivate?: () => void },
  ): void {
    if (opts.filled) {
      button.draggable = true;
      button.addEventListener("dragstart", (event) => {
        this.inventoryDragMoved = false;
        event.dataTransfer?.setData("text/plain", String(index));
        if (event.dataTransfer) {
          event.dataTransfer.effectAllowed = "move";
        }
        button.classList.add("is-dragging");
      });
      button.addEventListener("dragend", () => {
        button.classList.remove("is-dragging");
        document
          .querySelectorAll(".inventory-slot.is-drop-target")
          .forEach((el) => el.classList.remove("is-drop-target"));
      });
    }
    button.addEventListener("dragover", (event) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "move";
      }
      button.classList.add("is-drop-target");
    });
    button.addEventListener("dragleave", () => {
      button.classList.remove("is-drop-target");
    });
    button.addEventListener("drop", (event) => {
      event.preventDefault();
      button.classList.remove("is-drop-target");
      const from = Number(event.dataTransfer?.getData("text/plain"));
      if (!Number.isFinite(from) || from === index) {
        return;
      }
      this.inventoryDragMoved = true;
      this.send({ type: "swapInventory", fromIndex: from, toIndex: index });
    });
    if (opts.onActivate) {
      button.addEventListener("click", () => {
        if (this.inventoryDragMoved) {
          this.inventoryDragMoved = false;
          return;
        }
        opts.onActivate?.();
      });
    }
  }

  private renderDaily(you: PlayerPrivateState): void {
    const unlocked = you.hasCompletedFTUE;
    if (dailyLauncher) {
      dailyLauncher.hidden = !unlocked || this.dailyOpen;
    }
    if (dailyChallenge) {
      dailyChallenge.hidden = !unlocked || !this.dailyOpen;
    }
    if (!unlocked) {
      if (dailyWidget) {
        dailyWidget.hidden = true;
      }
      return;
    }

    const d = you.dailyChallenge;
    const task = dailyTaskById(d.taskId);
    const info = streakInfo(d.streakCount);
    const pct = Math.round((d.progress / Math.max(1, d.targetCount)) * 100);
    const claimReady = d.completed && !d.claimed;
    const loginReady = you.dailyLoginCalendar.lastClaimDateKey !== chicagoDateKey();
    const badgeCount = (claimReady ? 1 : 0) + (loginReady ? 1 : 0);

    if (dailyLauncherBadge) {
      dailyLauncherBadge.hidden = badgeCount <= 0;
      dailyLauncherBadge.textContent = String(badgeCount > 0 ? badgeCount : "");
    }

    if (dailyWidget) {
      dailyWidget.hidden = false;
      dailyWidget.innerHTML = `
        <button type="button" class="menu-row menu-row-icon" data-open-daily="1">
          <span class="row-ico">${iconSvg("calendar")}</span>
          <strong>Daily Challenges</strong>
          <em>${claimReady ? "Claim" : `${d.progress}/${d.targetCount}`}</em>
        </button>
        <p class="hint">Day ${d.streakCount} · ${info.xpMult}× lamp</p>
        ${claimReady ? `<button type="button" class="primary-btn" data-claim="1">${iconSvg("lamp")} Claim lamp</button>` : ""}
      `;
    }

    if (dailyChallengeBody && this.dailyOpen) {
      const weekKeys = recentDateKeys(d.dateKey, 7);
      const completed = new Set(d.completedDates);
      const weekHtml = weekKeys
        .map((key) => {
          const done = completed.has(key);
          const today = key === d.dateKey;
          return `<div class="daily-week-day ${done ? "is-done" : ""} ${today ? "is-today" : ""}">
            <span class="daily-week-letter">${chicagoWeekdayLetter(key)}</span>
            <span class="daily-week-mark">${done ? iconSvg("check", 14) : ""}</span>
          </div>`;
        })
        .join("");

      const trackHtml = DAILY_LAMP_TRACK.map((node, i) => {
        const reached = d.streakCount >= node.day;
        const last = i === DAILY_LAMP_TRACK.length - 1;
        return `<div class="daily-lamp-node ${reached ? "is-reached" : ""} ${last ? "is-star" : ""}">
          <span class="daily-lamp-dot">${last ? iconSvg("star", 12) : reached ? iconSvg("check", 12) : ""}</span>
          <span class="daily-lamp-label">${node.mult}× Day ${node.day}</span>
        </div>`;
      }).join("");

      const taskGlyph =
        task?.kind === "craft" ? "hammer" : task?.itemId.includes("stick") || task?.itemId.includes("twig") || task?.itemId.includes("hardwood")
          ? "stick"
          : "shovel";

      dailyChallengeBody.innerHTML = `
        <div class="daily-week">${weekHtml}</div>
        <div class="daily-lamp-track">${trackHtml}</div>
        <div class="daily-streak-row">
          <div class="daily-streak-badge">${d.streakCount}</div>
          <div class="daily-streak-copy">
            <strong>Streak ${d.streakCount}</strong>
            <p>Finish today.</p>
            <p class="soft">Miss a day and it starts over.</p>
          </div>
        </div>
        <div class="daily-task-card">
          <div class="daily-task-ico">${iconSvg(taskGlyph, 22)}</div>
          <div class="daily-task-copy">
            <strong>${escapeHtml(shortLabel(task?.label ?? d.taskId))}</strong>
            <div class="daily-task-bar-row">
              <div class="daily-task-bar"><div style="width:${pct}%"></div></div>
              <span>${d.progress} / ${d.targetCount}</span>
            </div>
          </div>
        </div>
        <button type="button" class="daily-claim-btn ${claimReady ? "is-ready" : ""}" data-claim="1" ${claimReady ? "" : "disabled"}>
          ${claimReady ? `${iconSvg("lamp", 16)} Claim lamp` : d.claimed ? "Claimed" : "Finish to claim"}
        </button>
        <button type="button" class="daily-link-btn" data-open-login="1">${iconSvg("sun", 14)} Daily login rewards</button>
      `;
    }
  }

  private openDailyChallenge(): void {
    if (!this.you?.hasCompletedFTUE) {
      return;
    }
    this.dailyOpen = true;
    this.renderDaily(this.you);
    // Ensure the panel isn't parked off-screen from an old drag save.
    if (dailyChallenge && gameRoot) {
      const rect = dailyChallenge.getBoundingClientRect();
      const parent = gameRoot.getBoundingClientRect();
      const maxX = Math.max(0, gameRoot.clientWidth - dailyChallenge.offsetWidth);
      const maxY = Math.max(0, gameRoot.clientHeight - Math.min(dailyChallenge.offsetHeight, 80));
      let x = rect.left - parent.left;
      let y = rect.top - parent.top;
      if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0 || x > maxX || y > maxY) {
        x = Math.min(12, maxX);
        y = Math.min(12, maxY);
      }
      dailyChallenge.style.left = `${x}px`;
      dailyChallenge.style.top = `${y}px`;
      dailyChallenge.style.right = "auto";
      dailyChallenge.style.bottom = "auto";
      dailyChallenge.style.transform = "none";
    }
  }

  private minimizeDailyChallenge(): void {
    this.dailyOpen = false;
    if (this.you) {
      this.renderDaily(this.you);
    } else if (dailyChallenge && dailyLauncher) {
      dailyChallenge.hidden = true;
      dailyLauncher.hidden = false;
    }
  }

  private closeLogin(): void {
    if (dailyLogin) {
      dailyLogin.hidden = true;
    }
  }

  private renderLoginPanel(you: PlayerPrivateState): void {
    if (!dailyLoginBody) {
      return;
    }
    const cal = you.dailyLoginCalendar;
    const today = chicagoDateKey();
    const claimedToday = cal.lastClaimDateKey === today;
    const nextDay = claimedToday ? Math.min(7, cal.streak) : Math.min(7, (cal.streak || 0) + 1);
    const days = Array.from({ length: 7 }, (_, i) => {
      const day = i + 1;
      const reward = loginReward(day);
      const item = ITEMS[reward.itemId];
      const done = claimedToday ? day <= cal.streak : day < nextDay;
      const current = !claimedToday && day === nextDay;
      const glyph =
        reward.itemId === "scroll_of_hospitality"
          ? "portal"
          : reward.itemId.includes("timber")
            ? "scroll"
            : reward.itemId.includes("volcanic")
              ? "hammer"
              : "sun";
      return `<div class="login-day-card ${done ? "is-done" : ""} ${current ? "is-current" : ""}">
        <span class="login-day-num">Day ${day}</span>
        <span class="login-day-ico">${iconSvg(glyph, 18)}</span>
        <strong>${escapeHtml(item.name)}</strong>
        <em>×${reward.quantity}</em>
      </div>`;
    }).join("");

    dailyLoginBody.innerHTML = `
      <div class="login-streak-row">
        <div class="daily-streak-badge">${cal.streak}</div>
        <div class="daily-streak-copy">
          <strong>${cal.streak > 0 ? `Streak ${cal.streak}` : "Start a streak"}</strong>
          <p>${claimedToday ? "Come back tomorrow." : "Claim today's boost gift."}</p>
        </div>
      </div>
      <div class="login-day-grid">${days}</div>
      <button type="button" class="daily-claim-btn ${claimedToday ? "" : "is-ready"}" id="claim-login" ${claimedToday ? "disabled" : ""}>
        ${claimedToday ? "Claimed today" : `${iconSvg("sun", 16)} Claim day ${nextDay}`}
      </button>
    `;
  }

  private renderNextSteps(you: PlayerPrivateState): void {
    if (!nextStepsEl) {
      return;
    }
    if (!you.hasCompletedFTUE) {
      nextStepsEl.hidden = false;
      const f = you.ftue;
      nextStepsEl.innerHTML = `<h2>${iconSvg("paw")} First walk</h2>
        ${stepRow(f.pebbles >= 3, `Pebbles ${Math.min(f.pebbles, 3)}/3`)}
        ${stepRow(f.boneShards >= 3, `Bones ${Math.min(f.boneShards, 3)}/3`)}
        ${stepRow(f.returnedToHub, "Back to hub")}
        <p class="hint">Dig a mound to get started.</p>`;
      return;
    }
    const show = !you.nextSteps.claimed;
    nextStepsEl.hidden = !show;
    if (!show) {
      return;
    }
    const s = you.nextSteps;
    const items = [
      ["Iron nugget", s.digIron],
      ["Fetch stick", s.fetchStick],
      ["Bronze treat", s.smeltBronze],
      ["Doggy Bank deposit", s.bankDeposit],
      ["Visit kennel", s.visitKennel],
    ] as const;
    nextStepsEl.innerHTML = `<h2>${iconSvg("flag")} Next</h2>${items
      .map(([label, done]) => stepRow(done, label))
      .join("")}${
      items.every(([, done]) => done)
        ? `<button type="button" class="primary-btn" id="claim-next">${iconSvg("portal")} Portal</button>`
        : ""
    }`;
    nextStepsEl.querySelector("#claim-next")?.addEventListener("click", () => this.send({ type: "claimNextSteps" }));
  }

  private renderBoosts(you: PlayerPrivateState): void {
    if (!boostMeter) {
      return;
    }
    const now = Date.now();
    const active = you.boosts.filter((b) => b.expiresAt > now);
    boostMeter.hidden = active.length === 0;
    boostMeter.innerHTML = active
      .map(
        (b) =>
          `<span class="boost-chip ${skillTintClass(b.skillId)}">${iconSvg(SKILL_GLYPH[b.skillId])} ${Math.ceil((b.expiresAt - now) / 1000)}s</span>`,
      )
      .join("");
  }

  private openTravel(message: Extract<ServerMessage, { type: "travelMenu" }>): void {
    const destinations = message.destinations;
    const you = message.you;
    const map = this.parkMap;
    const parkPins = you ? destinations.filter((d) => d.mapX != null && d.mapY != null) : [];
    const listed = you
      ? destinations.filter((d) => d.mapX == null || d.mapY == null)
      : destinations;
    const mapBody =
      you && parkPins.length > 0 && map
        ? travelParkMapHtml(you, parkPins, map)
        : `<p class="hint">Hop to a park landmark, or visit home.</p>`;

    this.modal(
      "Travel",
      "portal",
      `${mapBody}
       <div class="travel-list">
         ${listed
           .map((d) => {
             const state = d.youAreHere ? "here" : d.unlocked ? "go" : "lock";
             return `<button class="menu-row menu-row-icon" data-tag="${d.tag}" ${d.unlocked ? "" : "disabled"} title="${escapeHtml(d.description)}">
            <span class="row-ico">${iconSvg(travelGlyph(d.tag))}</span>
            <strong>${escapeHtml(d.name)}</strong>
            <em class="state-${state}">${state === "here" ? "Here" : state === "go" ? "Go" : "Lock"}</em>
          </button>`;
           })
           .join("")}
       </div>`,
      (root) => {
        paintTravelMapCanvas(root, map);
        root.querySelectorAll<HTMLButtonElement>("[data-tag]").forEach((btn) => {
          btn.addEventListener("click", () => {
            this.closeModal();
            this.send({ type: "travel", tag: btn.dataset.tag as TravelDestination["tag"] });
          });
        });
      },
    );
    this.travelModalOpen = true;
    const card = modalRoot.querySelector("#modal-card") as HTMLElement | null;
    if (card) {
      card.classList.add("modal-card-travel");
    }
  }

  private openRoster(entries: NeighborhoodEntry[]): void {
    this.modal(
      "Kennels",
      "home",
      entries.length === 0
        ? `<p class="hint">No kennels yet</p>`
        : `<div class="roster-grid">${entries
            .map(
              (e) => `
          <article class="roster-card">
            <strong>${escapeHtml(e.name)}</strong>
            <p class="roster-lv">Lv ${e.totalLevel}</p>
            <p class="chips">${iconSvg("paw")} ${e.guestCount}${e.visitLocked ? " locked" : ""}</p>
            <button ${e.canVisit ? "" : "disabled"} data-visit="${e.ownerKey}">${e.canVisit ? "Visit" : "Lock"}</button>
          </article>`,
            )
            .join("")}</div>`,
      (root) => {
        root.querySelectorAll<HTMLButtonElement>("[data-visit]").forEach((btn) => {
          btn.addEventListener("click", () => {
            this.closeModal();
            this.send({ type: "travel", tag: `VisitKennel:${btn.dataset.visit}` });
          });
        });
      },
    );
  }

  private openBank(): void {
    const you = this.you;
    if (!you) {
      return;
    }
    const refreshing = this.bankModalOpen;
    const slotQty = (qty: number) => (qty > 1 ? `<span class="qty">${qty}</span>` : "");
    const bagSlots = you.inventory
      .map((slot, i) => {
        if (!slot) {
          return `<button type="button" data-slot="${i}" class="inventory-slot bank-slot is-empty" aria-label="Empty bag slot"></button>`;
        }
        const skillId = itemSkillId(slot.itemId);
        const outline = skillId ? skillOutlineClass(skillId) : "";
        return `<button type="button" data-slot="${i}" data-dep="${i}" data-item="${slot.itemId}" data-qty="${slot.quantity}" class="inventory-slot bank-slot ${outline}" aria-label="${escapeHtml(ITEMS[slot.itemId].name)}"><span class="slot-glyph">${itemGlyph(slot.itemId)}</span>${slotQty(slot.quantity)}</button>`;
      })
      .join("");
    const vaultFilled = you.vault.slots
      .map((id) => {
        const qty = you.vault.items[id] ?? 0;
        if (qty <= 0) {
          return "";
        }
        const skillId = itemSkillId(id);
        const outline = skillId ? skillOutlineClass(skillId) : "";
        return `<button type="button" data-withdraw="${id}" data-item="${id}" data-qty="${qty}" class="inventory-slot bank-slot ${outline}" aria-label="${escapeHtml(ITEMS[id].name)}"><span class="slot-glyph">${itemGlyph(id)}</span>${slotQty(qty)}</button>`;
      })
      .join("");
    const emptyVault = Math.max(0, VAULT_SLOTS - you.vault.slots.filter((id) => (you.vault.items[id] ?? 0) > 0).length);
    const vaultEmpties = Array.from({ length: emptyVault }, () =>
      `<button type="button" class="inventory-slot bank-slot is-empty" disabled aria-hidden="true"></button>`,
    ).join("");
    this.modal(
      "Doggy Bank",
      "bank",
      `<div class="btn-row bank-actions"><button id="deposit-all" class="primary-btn">${iconSvg("bag")} All in</button></div>
       <div class="bank-cols">
         <section class="bank-col" aria-label="Bag">
           <h3>${iconSvg("bag")} Bag</h3>
           <div class="bank-grid">${bagSlots}</div>
         </section>
         <section class="bank-col" aria-label="Doggy Bank">
           <h3>${iconSvg("bank")} Doggy Bank</h3>
           <div class="bank-grid">${vaultFilled}${vaultEmpties}</div>
         </section>
       </div>`,
      (root) => {
        root.querySelector("#deposit-all")?.addEventListener("click", () => {
          this.send({ type: "bankDepositAll" });
        });
        root.querySelectorAll<HTMLButtonElement>("[data-slot]").forEach((btn) => {
          const index = Number(btn.dataset.slot);
          const filled = btn.dataset.dep != null;
          this.bindInventoryDnD(btn, index, {
            filled,
            onActivate: filled
              ? () => {
                  this.send({ type: "bankDeposit", slotIndex: index, quantity: 99 });
                }
              : undefined,
          });
        });
        root.querySelectorAll<HTMLButtonElement>("[data-withdraw]").forEach((btn) => {
          btn.addEventListener("click", () => {
            this.send({ type: "bankWithdraw", itemId: btn.dataset.withdraw as ItemId, quantity: 99 });
          });
        });
        root.querySelectorAll<HTMLButtonElement>("[data-item]").forEach((btn) => {
          const id = btn.dataset.item as ItemId;
          const qty = Number(btn.dataset.qty ?? 0) || undefined;
          this.bindItemTip(btn, id, qty);
        });
      },
      { skipEnterAnimation: refreshing },
    );
    this.bankModalOpen = true;
    modalRoot.querySelector("#modal-card")?.classList.add("modal-card-bank");
  }

  private openCraft(): void {
    const you = this.you;
    if (!you) {
      return;
    }
    const treats = RECIPES.filter((r) => r.category === "treat" || r.category === "misc");
    const toys = RECIPES.filter((r) => r.category === "tool");
    const row = (r: (typeof RECIPES)[number], level: number) => {
      const locked = level < r.reqLevel;
      const skillId = recipeSkillId(r);
      return `<button class="menu-row menu-row-icon" data-recipe="${r.id}" ${locked ? "disabled" : ""} title="${r.inputs.map((i) => `${i.quantity}× ${ITEMS[i.itemId].name}`).join(" + ")}">
           <span class="row-ico ${skillTintClass(skillId)}">${itemGlyph(r.output)}</span>
           <strong>${r.name}</strong>
           <em>Lv ${r.reqLevel}</em>
         </button>`;
    };
    this.modal(
      "Forge",
      "hammer",
      `<p class="hint">Treatcraft mixes snacks. Toycraft forges tools.</p>
       <h3 class="menu-subhead">Treatcraft · Lv ${you.skills.treatcraft.level}</h3>
       ${treats.map((r) => row(r, you.skills.treatcraft.level)).join("")}
       <h3 class="menu-subhead">Toycraft · Lv ${you.skills.toycraft.level}</h3>
       ${toys.map((r) => row(r, you.skills.toycraft.level)).join("")}`,
      (root) => {
        root.querySelectorAll<HTMLButtonElement>("[data-recipe]").forEach((btn) => {
          btn.addEventListener("click", () => {
            this.send({ type: "craft", recipeId: btn.dataset.recipe!, quantity: 1 });
            this.closeModal();
          });
        });
      },
    );
  }

  private renderGoalsBadge(you: PlayerPrivateState): void {
    if (!goalsMenuBadge) {
      return;
    }
    const ready = countGoalsReady(you);
    goalsMenuBadge.hidden = ready <= 0;
    goalsMenuBadge.textContent = ready > 0 ? String(ready) : "";
    if (goalsMenuBtn) {
      goalsMenuBtn.title = ready > 0 ? `Goals (${ready} to claim)` : "Goals";
      goalsMenuBtn.setAttribute("aria-label", ready > 0 ? `Goals, ${ready} ready to claim` : "Goals");
    }
  }

  private openAchievements(): void {
    const you = this.you;
    if (!you) {
      return;
    }
    const refreshing = this.goalsModalOpen;
    const chapters = achievementChapters();
    if (this.goalsChapter < 0 || this.goalsChapter >= chapters.length) {
      this.goalsChapter = 0;
    }
    const chapter = chapters[this.goalsChapter]!;
    const claimed = new Set(you.achievementBook.claimed);
    const progress = {
      skills: you.skills,
      visitedAreas: you.visitedAreas,
      lifetimeGathered: you.lifetimeGathered,
      lifetimeCrafted: you.lifetimeCrafted,
    };
    const ranked = [...chapter.achievements].sort((a, b) => {
      const ar = !claimed.has(a.id) && isAchievementMet(progress, a.id) ? 0 : claimed.has(a.id) ? 2 : 1;
      const br = !claimed.has(b.id) && isAchievementMet(progress, b.id) ? 0 : claimed.has(b.id) ? 2 : 1;
      return ar - br;
    });
    const readyCount = ranked.filter((a) => !claimed.has(a.id) && isAchievementMet(progress, a.id)).length;
    if (!this.goalsSelectedId || !ranked.some((a) => a.id === this.goalsSelectedId)) {
      this.goalsSelectedId = ranked.find((a) => !claimed.has(a.id) && isAchievementMet(progress, a.id))?.id
        ?? ranked.find((a) => !claimed.has(a.id))?.id
        ?? ranked[0]?.id
        ?? null;
    }
    const selected = ranked.find((a) => a.id === this.goalsSelectedId) ?? ranked[0] ?? null;
    const prev = chapters[(this.goalsChapter - 1 + chapters.length) % chapters.length]!;
    const next = chapters[(this.goalsChapter + 1) % chapters.length]!;
    const prevReady = countChapterReady(you, prev.achievements);
    const nextReady = countChapterReady(you, next.achievements);

    const rows = ranked
      .map((a) => {
        const done = claimed.has(a.id);
        const ready = !done && isAchievementMet(progress, a.id);
        const selectedCls = a.id === selected?.id ? " is-selected" : "";
        return `<button type="button" class="goals-row${selectedCls}${ready ? " is-ready" : ""}${done ? " is-done" : ""}" data-ach="${a.id}">
          <span class="goals-row-ico">${iconSvg(chapter.glyph, 18)}</span>
          <strong>${escapeHtml(a.name)}</strong>
          ${ready ? `<span class="goals-row-claim">CLAIM</span>` : ""}
          <span class="goals-row-star" aria-hidden="true">${iconSvg(done ? "check" : "star", 12)}</span>
        </button>`;
      })
      .join("");

    const detail = selected
      ? goalsDetailHtml(selected, chapter.glyph, claimed.has(selected.id), !claimed.has(selected.id) && isAchievementMet(progress, selected.id))
      : `<p class="hint">Pick a goal.</p>`;

    this.modal(
      "Goals",
      "book",
      `<div class="goals-book">
        <section class="goals-page goals-list-page">
          <header class="goals-page-head">
            <h3>${escapeHtml(chapter.title)}</h3>
            <p>${readyCount > 0 ? `${readyCount} ready to claim · good dog!` : "Little park feats to chase"}</p>
          </header>
          <div class="goals-rows">${rows}</div>
          <footer class="goals-nav">
            <p class="goals-nav-meta">${escapeHtml(chapter.title)} · ${this.goalsChapter + 1} of ${chapters.length}${readyCount ? ` · ${readyCount} to claim` : ""}</p>
            <div class="goals-nav-btns">
              <button type="button" class="goals-nav-btn" data-chapter-delta="-1">
                ◀ ${escapeHtml(prev.title)}
                ${prevReady ? `<span class="goals-nav-badge">${prevReady}</span>` : ""}
              </button>
              <button type="button" class="goals-nav-btn" data-chapter-delta="1">
                ${escapeHtml(next.title)} ▶
                ${nextReady ? `<span class="goals-nav-badge">${nextReady}</span>` : ""}
              </button>
            </div>
          </footer>
        </section>
        <div class="goals-spine" aria-hidden="true"></div>
        <section class="goals-page goals-detail-page">${detail}</section>
      </div>`,
      (root) => {
        root.querySelectorAll<HTMLButtonElement>("[data-ach]").forEach((btn) => {
          btn.addEventListener("click", (event) => {
            const id = btn.dataset.ach!;
            if ((event.target as HTMLElement).closest(".goals-row-claim")) {
              this.closeModal();
              this.openLamp("ach", id);
              return;
            }
            this.goalsSelectedId = id;
            this.openAchievements();
          });
        });
        root.querySelectorAll<HTMLButtonElement>("[data-chapter-delta]").forEach((btn) => {
          btn.addEventListener("click", () => {
            const delta = Number(btn.dataset.chapterDelta);
            this.goalsChapter = (this.goalsChapter + delta + chapters.length) % chapters.length;
            this.goalsSelectedId = null;
            this.openAchievements();
          });
        });
        root.querySelector("#goals-claim")?.addEventListener("click", () => {
          if (!selected) {
            return;
          }
          this.closeModal();
          this.openLamp("ach", selected.id);
        });
      },
      { skipEnterAnimation: refreshing },
    );
    this.goalsModalOpen = true;
    const card = modalRoot.querySelector("#modal-card") as HTMLElement | null;
    card?.classList.add("modal-card-goals");
    const close = modalRoot.querySelector("#modal-close") as HTMLElement | null;
    close?.classList.add("goals-close");
    if (!refreshing && card && gameRoot) {
      const width = Math.min(720, gameRoot.clientWidth - 24);
      const maxX = Math.max(0, gameRoot.clientWidth - width);
      card.style.left = `${Math.round(maxX * 0.5)}px`;
      card.style.top = `${Math.round(Math.min(Math.max(0, gameRoot.clientHeight - 120) * 0.12, 64))}px`;
    }
  }

  private openCosmetics(): void {
    const you = this.you;
    if (!you) {
      return;
    }
    const current = Math.abs(you.color | 0) % DOG_BREEDS.length;
    const breedCards = DOG_BREEDS.map((breed, index) => {
      const selected = index === current ? " is-selected" : "";
      const url = `${DOG_PACK_ROOT}/${breed.folder}/${breed.idleFile}`;
      return `<button type="button" class="cosmetics-breed${selected}" data-breed="${index}" title="${escapeHtml(breed.label)}">
        <span class="breed-thumb" style="background-image:url('${url}')" aria-hidden="true"></span>
        <span class="breed-name">${escapeHtml(breed.label)}</span>
      </button>`;
    }).join("");

    const gear = [
      { id: "hat", label: "Hat", glyph: "hat", blurb: "Coming soon" },
      { id: "crown", label: "Crown", glyph: "crown", blurb: "Coming soon" },
      { id: "chain", label: "Chain", glyph: "chain", blurb: "Coming soon" },
    ]
      .map(
        (slot) => `<div class="cosmetics-slot is-locked" data-slot="${slot.id}">
          <span class="cosmetics-slot-ico">${iconSvg(slot.glyph, 22)}</span>
          <div>
            <strong>${escapeHtml(slot.label)}</strong>
            <p>${escapeHtml(slot.blurb)}</p>
          </div>
        </div>`,
      )
      .join("");

    this.modal(
      "Cosmetics",
      "sparkle",
      `<div class="cosmetics-menu">
        <section>
          <header class="cosmetics-head">
            <h3>Breed</h3>
            <p>Change anytime - your look updates right away.</p>
          </header>
          <div class="breed-grid cosmetics-breed-grid">${breedCards}</div>
        </section>
        <section>
          <header class="cosmetics-head">
            <h3>Gear shop</h3>
            <p>Hats, crowns, chains, and more - buy what you like when it lands.</p>
          </header>
          <div class="cosmetics-slots">${gear}</div>
        </section>
      </div>`,
      (root) => {
        root.querySelectorAll<HTMLButtonElement>("[data-breed]").forEach((btn) => {
          btn.addEventListener("click", () => {
            const breed = Number(btn.dataset.breed);
            if (!Number.isInteger(breed)) {
              return;
            }
            this.send({ type: "setAppearance", color: breed });
            if (this.you) {
              this.you.color = breed;
            }
            root.querySelectorAll(".cosmetics-breed").forEach((el) => {
              el.classList.toggle("is-selected", el === btn);
            });
          });
        });
      },
    );
  }

  private openLogin(): void {
    const you = this.you;
    if (!you) {
      return;
    }
    this.renderLoginPanel(you);
    if (dailyLogin) {
      dailyLogin.hidden = false;
    }
  }

  private openLamp(kind: "daily" | "ach", achievementId?: string): void {
    const you = this.you;
    if (!you) {
      return;
    }
    const preview = (id: SkillId) =>
      kind === "daily"
        ? lampXp(you.skills[id].level, you.dailyChallenge.streakCount)
        : achievementLampXp(you.skills[id].level, achievementById(achievementId ?? "")?.lamp ?? "small");
    this.modal(
      "Lamp",
      "lamp",
      `<div class="lamp-grid">${(Object.keys(SKILLS) as SkillId[])
        .map(
          (id) => `<button data-skill="${id}" class="${skillTintClass(id)}${id === this.claimSkill ? " is-active" : ""}" title="${SKILLS[id].name}">
            ${iconSvg(SKILL_GLYPH[id])}
            <strong>${you.skills[id].level}</strong>
            <small>+${preview(id).toLocaleString()}</small>
          </button>`,
        )
        .join("")}</div>
       <div class="btn-row">
         <button class="primary-btn" id="lamp-go">Claim</button>
         <button class="ghost-btn" id="lamp-cancel">Back</button>
       </div>`,
      (root) => {
        root.querySelectorAll<HTMLButtonElement>("[data-skill]").forEach((btn) => {
          btn.addEventListener("click", () => {
            this.claimSkill = btn.dataset.skill as SkillId;
            this.closeModal();
            this.openLamp(kind, achievementId);
          });
        });
        root.querySelector("#lamp-cancel")?.addEventListener("click", () => {
          this.closeModal();
          if (kind === "ach") {
            this.openAchievements();
          }
        });
        root.querySelector("#lamp-go")?.addEventListener("click", () => {
          if (kind === "daily") {
            this.send({ type: "claimDaily", skillId: this.claimSkill });
          } else if (achievementId) {
            this.send({ type: "claimAchievement", achievementId, skillId: this.claimSkill });
          }
          this.closeModal();
          if (kind === "ach") {
            // Refresh book after claim sync arrives; reopen so progress updates soon.
            window.setTimeout(() => this.openAchievements(), 120);
          }
        });
      },
    );
  }

  private confirm(hostName: string, onYes: () => void): void {
    this.modal(
      "Visit",
      "home",
      `<p class="confirm-name">${escapeHtml(hostName)}</p>
       <div class="btn-row">
         <button class="primary-btn" id="yes">Visit</button>
         <button class="ghost-btn" id="no">Stay</button>
       </div>`,
      (root) => {
        root.querySelector("#yes")?.addEventListener("click", () => {
          this.closeModal();
          onYes();
        });
        root.querySelector("#no")?.addEventListener("click", () => this.closeModal());
      },
    );
  }

  private modal(
    title: string,
    glyph: string,
    body: string,
    bind?: (root: HTMLElement) => void,
    opts?: { skipEnterAnimation?: boolean },
  ): void {
    this.travelModalOpen = false;
    this.bankModalOpen = false;
    if (!opts?.skipEnterAnimation) {
      this.goalsModalOpen = false;
    }
    modalRoot.hidden = false;
    modalRoot.classList.remove("is-leaving");
    if (!opts?.skipEnterAnimation) {
      modalRoot.classList.remove("is-visible");
    }
    modalRoot.innerHTML = `<div class="modal-backdrop" id="modal-backdrop"></div>
    <div class="modal-card ui-panel ui-draggable" id="modal-card">
      <header class="modal-head ui-drag-handle" data-drag="modal">
        <span class="panel-glyph">${iconSvg(glyph)}</span>
        <h2>${escapeHtml(title)}</h2>
        <button type="button" class="game-panel-close" id="modal-close" aria-label="Close">×</button>
      </header>
      <div class="modal-body">${body}</div>
    </div>`;
    modalRoot.querySelector("#modal-close")?.addEventListener("click", () => this.closeModal());
    modalRoot.querySelector("#modal-backdrop")?.addEventListener("click", () => this.closeModal());
    const card = modalRoot.querySelector("#modal-card") as HTMLElement | null;
    const handle = modalRoot.querySelector<HTMLElement>('[data-drag="modal"]');
    if (card && handle && gameRoot) {
      // Center once, then allow dragging inside the park viewport.
      const maxX = Math.max(0, gameRoot.clientWidth - Math.min(420, gameRoot.clientWidth - 24));
      const maxY = Math.max(0, gameRoot.clientHeight - 120);
      card.style.left = `${Math.round(maxX * 0.5)}px`;
      card.style.top = `${Math.round(Math.min(maxY * 0.18, 80))}px`;
      card.style.right = "auto";
      card.style.bottom = "auto";
      enableDrag({ handle, panel: card, bounds: gameRoot, storageKey: "dogdell-ui-modal" });
    }
    bind?.(modalRoot);
    if (opts?.skipEnterAnimation) {
      modalRoot.classList.add("is-visible");
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          modalRoot.classList.add("is-visible");
        });
      });
    }
  }

  private closeModal(): void {
    if (modalRoot.hidden) {
      this.finishCloseModal();
      return;
    }
    const card = modalRoot.querySelector("#modal-card") as HTMLElement | null;
    if (!card || !modalRoot.classList.contains("is-visible") || modalRoot.classList.contains("is-leaving")) {
      this.finishCloseModal();
      return;
    }
    const finish = (): void => {
      card.removeEventListener("transitionend", onEnd);
      this.finishCloseModal();
    };
    const onEnd = (event: TransitionEvent): void => {
      if (event.target !== card || event.propertyName !== "opacity") {
        return;
      }
      finish();
    };
    modalRoot.classList.remove("is-visible");
    modalRoot.classList.add("is-leaving");
    card.addEventListener("transitionend", onEnd);
    window.setTimeout(finish, 360);
  }

  private finishCloseModal(): void {
    this.hideItemTip();
    this.travelModalOpen = false;
    this.bankModalOpen = false;
    this.goalsModalOpen = false;
    modalRoot.hidden = true;
    modalRoot.classList.remove("is-visible", "is-leaving");
    modalRoot.replaceChildren();
  }
}

function stepRow(done: boolean, label: string): string {
  return `<p class="step-row ${done ? "done" : ""}">${iconSvg(done ? "check" : "dot")} ${escapeHtml(label)}</p>`;
}

function countChapterReady(you: PlayerPrivateState, list: readonly AchievementDef[]): number {
  const claimed = new Set(you.achievementBook.claimed);
  const progress = {
    skills: you.skills,
    visitedAreas: you.visitedAreas,
    lifetimeGathered: you.lifetimeGathered,
    lifetimeCrafted: you.lifetimeCrafted,
  };
  return list.filter((a) => !claimed.has(a.id) && isAchievementMet(progress, a.id)).length;
}

function countGoalsReady(you: PlayerPrivateState): number {
  return achievementChapters().reduce((sum, chapter) => sum + countChapterReady(you, chapter.achievements), 0);
}

function goalsDetailHtml(def: AchievementDef, glyph: string, done: boolean, ready: boolean): string {
  const status = done
    ? `<p class="goals-status is-done">Already claimed · nice work</p>`
    : ready
      ? `<p class="goals-status is-ready">Ready · claim your lamp XP</p>`
      : `<p class="goals-status">Still sniffing this one out</p>`;
  return `
    <div class="goals-detail-top">
      <span class="goals-detail-ico">${iconSvg(glyph, 28)}</span>
      <div>
        <h3>${escapeHtml(def.name)}</h3>
        ${status}
      </div>
    </div>
    <p class="goals-desc">${escapeHtml(def.description)}</p>
    <p class="goals-reward"><strong>Reward:</strong> ${escapeHtml(lampRewardCopy(def.lamp))}</p>
    ${
      ready
        ? `<button type="button" class="goals-claim-btn" id="goals-claim">Claim Reward</button>`
        : done
          ? `<button type="button" class="goals-claim-btn" disabled>Claimed</button>`
          : `<button type="button" class="goals-claim-btn" disabled>Keep going</button>`
    }
  `;
}

function shortLabel(label: string): string {
  return label.replace(/\s*[---]\s*.*$/, "").trim();
}

function travelGlyph(tag: string): string {
  if (tag.includes("Dig")) return "shovel";
  if (tag.includes("Fetch")) return "paw";
  if (tag.includes("Basket") || tag.includes("Court")) return "ball";
  if (tag.includes("Meadow") || tag.includes("Pond")) return "berry";
  if (tag.includes("Forge")) return "hammer";
  if (tag.includes("Vault") || tag.includes("Bank")) return "bank";
  if (tag.includes("Kennel") || tag.includes("Neighborhood")) return "home";
  if (tag === "Hub") return "paw";
  return "portal";
}

/** Full park map using live terrain tiles - pins at real landmark coordinates. */
function travelParkMapHtml(
  you: { x: number; y: number },
  pins: TravelDestination[],
  map: MapData,
): string {
  const w = Math.max(1, map.width);
  const h = Math.max(1, map.height);
  const hereName = pins.find((d) => d.youAreHere)?.name ?? null;
  const pinHtml = pins
    .filter((d) => !d.youAreHere)
    .map((d) => {
      const left = (((d.mapX ?? 0) + 0.5) / w) * 100;
      const top = (((d.mapY ?? 0) + 0.5) / h) * 100;
      const state = d.unlocked ? "go" : "lock";
      const short = d.name.replace(/^Community\s+/i, "").split(" ")[0] ?? d.name;
      return `<button type="button" class="travel-pin state-${state}" data-tag="${d.tag}" style="left:${left.toFixed(2)}%;top:${top.toFixed(2)}%" title="${escapeHtml(d.description)}" ${d.unlocked ? "" : "disabled"}>
        <span class="travel-pin-ico">${iconSvg(travelGlyph(d.tag), 22)}</span>
        <span class="travel-pin-label">${escapeHtml(short)}</span>
      </button>`;
    })
    .join("");
  const youLeft = ((you.x + 0.5) / w) * 100;
  const youTop = ((you.y + 0.5) / h) * 100;
  const youTitle = hereName ? `You are here · ${hereName}` : "You are here";
  const youBody = hereName
    ? `<span class="travel-you-here">You are here</span><strong class="travel-you-place">${escapeHtml(hereName)}</strong>`
    : `<span>You</span>`;
  return `<div class="travel-map-wrap">
    <p class="hint travel-map-hint">Tap a landmark to hop.</p>
    <div class="travel-map" role="group" aria-label="Park travel map">
      <canvas class="travel-map-canvas" width="720" height="720" aria-hidden="true"></canvas>
      <div class="travel-you${hereName ? " has-place" : ""}" title="${escapeHtml(youTitle)}" style="left:${youLeft.toFixed(2)}%;top:${youTop.toFixed(2)}%">${youBody}</div>
      ${pinHtml}
    </div>
  </div>`;
}

function paintTravelMapCanvas(root: HTMLElement, map: MapData | null): void {
  const canvas = root.querySelector(".travel-map-canvas") as HTMLCanvasElement | null;
  if (!canvas || !map) {
    return;
  }
  const bake = bakeStylizedTravelMap(map, 720);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }
  canvas.width = bake.width;
  canvas.height = bake.height;
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(bake, 0, 0);
}

function itemGlyphKind(id: ItemId): string {
  if (id.includes("shovel")) return "shovel";
  if (id.includes("ball") || id.includes("rope")) return "ball";
  if (id.includes("collar")) return "collar";
  if (id.includes("treat")) return "treat";
  if (id.includes("scroll") || id.includes("elixir")) return "scroll";
  if (id.includes("stick") || id.includes("twig") || id.includes("branch")) return "stick";
  if (id.includes("bone") || id.includes("nugget") || id.includes("pebble") || id.includes("tag") || id.includes("medallion") || id.includes("chew") || id.includes("charcoal")) {
    return "ore";
  }
  if (id.includes("berry") || id.includes("apple") || id.includes("crumb") || id.includes("cheese") || id.includes("picnic") || id.includes("truffle") || id.includes("nibble")) {
    return "berry";
  }
  if (id.includes("biscuit") || id.includes("pretzel") || id.includes("scone") || id.includes("hotdog") || id.includes("croissant") || id.includes("steak") || id.includes("handout")) {
    return "treat";
  }
  return "bag";
}

function itemColorHex(id: ItemId): string {
  const color = ITEMS[id]?.color ?? 0xc4a574;
  return `#${color.toString(16).padStart(6, "0")}`;
}

function itemGlyph(id: ItemId): string {
  return filledItemIcon(itemGlyphKind(id), itemColorHex(id));
}

/** Compact brand icons (stroke SVG). */
function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
